import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router";
import { RequireAuth } from "./routes/RequireAuth";
import { Login } from "./routes/Login";
import { AuthCallback } from "./routes/AuthCallback";
import { Consultations } from "./routes/Consultations";
import { Profile } from "./routes/Profile";
import { MindfulMeProfile } from "./routes/mindfulme/MindfulMeProfile";
import { ManageSubscription } from "./routes/mindfulme/ManageSubscription";
import { CancelSubscription } from "./routes/mindfulme/CancelSubscription";
import { Landing } from "./routes/Landing";
import { PublicShell } from "./components/PublicShell";
import { Card, CardContent, CardHeader } from "./components/ui/card";
import { getTenantConfig } from "./lib/tenant";

const lazyNamed = <T extends Record<string, React.ComponentType>>(
  load: () => Promise<T>,
  name: keyof T,
) => lazy(() => load().then((m) => ({ default: m[name] })));

const AdminRoutesEntry = lazyNamed(() => import("./routes/AdminRoutesEntry"), "AdminRoutesEntry");
const PlayerRoutesEntry = lazyNamed(() => import("./routes/PlayerRoutesEntry"), "PlayerRoutesEntry");
const Catalog = lazyNamed(() => import("./routes/Catalog"), "Catalog");
const CourseDetail = lazyNamed(() => import("./routes/CourseDetail"), "CourseDetail");
const Library = lazyNamed(() => import("./routes/Library"), "Library");
const LibraryItem = lazyNamed(() => import("./routes/LibraryItem"), "LibraryItem");
const PottyTraining = lazyNamed(() => import("./routes/PottyTraining"), "PottyTraining");
const SleepTraining = lazyNamed(() => import("./routes/SleepTraining"), "SleepTraining");
const ReportPage = lazyNamed(() => import("./routes/ReportPage"), "ReportPage");
const ResultsListPage = lazyNamed(() => import("./routes/ResultsListPage"), "ResultsListPage");

export function App() {
  const tenant = getTenantConfig();
  const has = (f: Parameters<typeof tenant.features.has>[0]) => tenant.features.has(f);

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public marketing landing */}
        <Route path="/" element={tenant.homePath === "/courses" ? <Landing /> : <Navigate to={tenant.homePath} replace />} />

        {/* Auth flows stay shell-less */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        <Route element={<PublicShell />}>
          {has("potty") && <Route path="/potty/potty-training" element={<PottyTraining />} />}
          {has("sleep") && <Route path="/sleep/sleep-training" element={<SleepTraining />} />}
          <Route
            path="/terms"
            element={<PlaceholderPage title="Terms of Service" body="Phase 7 — placeholder." />}
          />
          <Route
            path="/privacy"
            element={<PlaceholderPage title="Privacy Policy" body="Phase 7 — placeholder." />}
          />

          {/* Authenticated routes inside the same shell */}
          <Route element={<RequireAuth />}>
            {has("courses") && <Route path="/courses" element={<Catalog />} />}
            {has("courses") && <Route path="/courses/:slug" element={<CourseDetail />} />}

            {has("library") && <Route path="/library" element={<Library />} />}
            {has("library") && <Route path="/library/:id" element={<LibraryItem />} />}
            <Route path="/profile" element={tenant.id === "mindfulme" ? <MindfulMeProfile /> : <Profile />} />
            {/* MindfulMe only: manage / cancel the love-funnel Stripe subscriptions */}
            {tenant.id === "mindfulme" && <Route path="/profile/subscription" element={<ManageSubscription />} />}
            {tenant.id === "mindfulme" && (
              <Route path="/profile/subscription/:id/cancel" element={<CancelSubscription />} />
            )}
            {has("consultations") && <Route path="/consultations" element={<Consultations />} />}
            {has("report") && <Route path="/report" element={<ResultsListPage />} />}
            {has("report") && <Route path="/report/detail" element={<ReportPage />} />}
            {has("report") && <Route path="/results" element={<Navigate to="/report" replace />} />}
          </Route>
        </Route>

        {has("admin") && <Route path="/admin/*" element={<AdminRoutesEntry />} />}
        {has("player") && <Route path="/courses/:slug/play/*" element={<PlayerRoutesEntry />} />}

        {/* Catch-all -> tenant home */}
        <Route path="*" element={<Navigate to={tenant.homePath} replace />} />
      </Routes>
    </Suspense>
  );
}


/**
 * Tiny inline placeholder used for the Phase-7-stubbed /terms + /privacy
 * routes. Renders inside <PublicShell /> so it inherits the canonical chrome.
 */
function PlaceholderPage({ title, body }: { title: string; body: string }) {
  // Quick task 260512-q7q — wrap the placeholder in a Card so /terms and
  // /privacy stop looking like empty unstyled pages. <h1> is preserved for
  // a11y (CardTitle is a <div> in our shadcn variant), rendered inside the
  // Card. Copy + props unchanged.
  return (
    <div className="mx-auto max-w-2xl px-4 py-12 lg:px-6">
      <Card className="hover:shadow-elev-2 transition-shadow">
        <CardHeader>
          <h1 className="text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-3xl">
            {title}
          </h1>
        </CardHeader>
        <CardContent>
          <p className="text-base leading-relaxed text-muted-foreground">{body}</p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Suspense fallback for lazy-loaded route subtrees. aria-busy + visually
 * empty mirrors the RequireAuth/RequireAdmin loading-frame pattern so the
 * chunk-fetch latency doesn't trigger a layout flash.
 */
function RouteFallback() {
  return (
    <div
      className="min-h-dvh bg-background"
      aria-busy="true"
      aria-label="Loading"
    />
  );
}
